import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { CustomMessagesService } from '@core/errors/custom-messages.service';
import { User } from '@core/interfaces';
import { getErrorCode } from '@core/utils/get-error-code';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { PasswordValidation } from '@validators/password-validation';
import { UsersService } from 'app/main/settings/accounts/users/services/users-resolver.service';
import { sentry } from 'sentry';
import { ChangePasswordService } from './change-password.service';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';

@Component({
  selector: 'change-password-dialog',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ChangePasswordComponent implements OnInit {
  public isLoading: boolean;
  public showPassword: boolean;
  public showNewPassword: boolean;
  public showNewPasswordConfirm: boolean;
  public passwordMatches: any[];
  public isOnEditDialog: boolean;
  public passwordForm: FormGroup;
  public userType: string;

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly user: User,
    private readonly formBuilder: FormBuilder,
    private readonly usersService: UsersService,
    private readonly changePasswordService: ChangePasswordService,
    private readonly customMessageService: CustomMessagesService,
    private readonly fuseProgressBarService: FuseProgressBarService,
    public readonly matDialogRef: MatDialogRef<ChangePasswordComponent>,
    private readonly fuseTranslationLoaderService: FuseTranslationLoaderService,
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
    this.isLoading = false;
    this.showPassword = false;
    this.showNewPassword = false;
    this.showNewPasswordConfirm = false;
    this.isOnEditDialog = !!(this.user.id);
    this.userType = this.user['__typename'];
    this.passwordMatches = [
      { translate: 'CHARACTERS', regex: '^.{8,}$' },
      { translate: 'UPPER_CASE_LETTER', regex: '[A-Z]{1,}' },
      { translate: 'LOWER_CASE_LETTER', regex: '[a-z]{1,}' },
      { translate: 'ONE_DIGIT', regex: '[0-9]{1,}' },
      { translate: 'ONE_SPECIAL_CHARACTER', regex: '[^a-zA-Z0-9]{1,}' }
    ];
  }

  ngOnInit(): void {
    this.createPasswordForm();
  }

  private createPasswordForm(): void {
    const passwordForm = {
      password: [null, [
        Validators.required,
        Validators.minLength(8),
        Validators.pattern(new RegExp('[0-9]{1,}')),
        Validators.pattern(new RegExp('[a-z]{1,}')),
        Validators.pattern(new RegExp('[A-Z]{1,}')),
        Validators.pattern(new RegExp('[^a-zA-Z0-9]{1,}'))
      ]],
      confirmPassword: [null, Validators.required]
    };

    if (this.userType === 'AuthenticatedUser') {
      passwordForm['currentPassword'] = [null, Validators.required];
    }

    this.passwordForm = this.formBuilder.group(passwordForm, {
      validator: PasswordValidation.validate.bind(this)
    });

    if (this.userType === 'User') {
      this.passwordForm.get('password').setValue(this.changePasswordService.generatePassword());
      this.showNewPassword = true;
    }
  }

  public matchValue(regex: string, value: string | null): boolean {
    if (!value) {
      return false;
    }

    return RegExp(regex).test(value);
  }

  public async saveNewPassword(): Promise<void> {
    try {
      this.isLoading = true;
      this.fuseProgressBarService.show();

      if (!this.isOnEditDialog) {
        await this.getNewUserPassword();
        return;
      }

      await this.updateAnExistingUsersPassword();
    } catch (err) {
      const code = getErrorCode(err);

      if (code === 'NewUserPasswordHasBeenUsedBefore') {
        this.passwordForm.get('password').setErrors({ usedBefore: true });
        return;
      }

      if (code === 'InvalidPasswordException' || code === 'NotAuthorizedException') {
        this.passwordForm.get('currentPassword').setErrors({ invalid: true });
        return;
      }

      if (code === 'LimitExceededException') {
        this.matDialogRef.close();
        this.customMessageService.error('CHANGE_PASSWORD.MESSAGES.TOO_MANY_REQUESTS_ERROR');
        return;
      }

      sentry.captureException(err);
      this.customMessageService.error('CHANGE_PASSWORD.MESSAGES.CHANGE_PASSWORD_ERROR');
    } finally {
      this.isLoading = false;
      this.fuseProgressBarService.hide();
      this.matDialogRef.disableClose = false;
    }
  }

  private async getNewUserPassword(): Promise<void> {
    await this.matDialogRef.close({
      password: this.passwordForm.value.password
    });
  }

  private async updateAnExistingUsersPassword(): Promise<void> {
    this.matDialogRef.disableClose = true;

    if (this.userType === 'User') {
      await this.usersService.setUserPassword(this.user.id, {
        password: this.passwordForm.value.password,
        permanentPassword: false
      }).toPromise();
    }

    if (this.userType === 'AuthenticatedUser') {
      await this.changePasswordService.changeUserPassword({
        newPassword: this.passwordForm.value.password,
        oldPassword: this.passwordForm.value.currentPassword
      });
    }

    this.customMessageService.success('CHANGE_PASSWORD.MESSAGES.CHANGE_PASSWORD_SUCCESS');
    this.matDialogRef.close();
  }
}
